import { css } from '@emotion/react'
import api from 'api'
import { HeadingSection, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { ContextualHelpCard } from 'components/ContextualHelpCard'
import { useFlags } from 'config/useFlagsContext'
import { useSolicitacoesExameUnpagedQuery } from 'graphql/hooks.generated'
import { RastreioCancerInput } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import iconEsusRegulacao from 'images/contextual-help-card/icon-esus-regulacao.svg'
import { Fragment, useCallback, useMemo } from 'react'
import { Route, useHistory, useRouteMatch } from 'react-router'
import { isEmpty } from 'util/checks'
import { MetaArray } from 'util/metaPath'
import { getEditAtendProfId } from 'view/atendimentos/atendimento-individual/util-atendimentoIndividual'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { EsusRegulacaoModalForm } from '../esus-regulacao/esus-regulacao-form/EsusRegulacaoModalForm'
import { IntegracaoRegulacaoCard } from '../esus-regulacao/IntegracaoRegulacaoCard'
import {
  SolicitacaoRegulacaoModel,
  SOLICITAR_REGULACAO_URL,
  TipoSolicitacaoRegulacaoEnum,
} from '../esus-regulacao/model-esusRegulacao'
import { SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME } from '../tabs'
import { AdicionarSolicitacaoExameButtons } from './AdicionarSolicitacaoExameButtons'
import {
  convertHistoricoSolicitacaoExameToImpressao,
  convertSolicitacaoFromAtendToSolicitacaoWithInfo,
} from './converter-solicitacaoExames'
import SolicitacaoExamesTable from './ExamesTable'
import { CODIGO_CITOPATOLOGICOS, CODIGO_MAMOGRAFIAS, SolicitacaoExameModel } from './model-solicitacaoExames'
import { SolicitarExamesRootView } from './SolicitarExamesRootView'
import {
  getRastreioCitoPreenchido,
  getRastreioMamografiaPreenchido,
  joinSolicitacoesExame,
} from './utils-solicitacaoExames'

export interface ExamesViewProps {
  name: MetaArray<SolicitacaoExameModel>
  cidadao: CidadaoAtendimento
  prontuarioId: ID
  atendimentoId: ID
  atendimentoProfissional: AtendimentoProfissional
  isObservacaoAndAuxiliar: boolean
}

export function ExamesView(props: Readonly<ExamesViewProps>) {
  const { name, cidadao, prontuarioId, atendimentoId, atendimentoProfissional, isObservacaoAndAuxiliar } = props

  const alert = useAlert()
  const { analytics } = useAnalytics()
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()
  const {
    PILOTO_SOLICITACAO_EXAME_CITOPATOLOGICO_UTERINO_ENABLED,
    SOLICITACAO_EXAME_MAMOGRAFIA_ENABLED,
    API_ESUS_REGULACAO_ENABLED,
  } = useFlags()

  const history = useHistory()
  const match = useRouteMatch()

  const classes = createStyles()

  const {
    observacao: { isAtendimentoObservacao },
    tiposAtendimento: { isAtendimentoProcedimentos },
  } = useAtendimentoContext()

  const {
    handleSubmit,
    removeItem,
    handleRowChanged,
    input: { value: solicitacoesExameFromAtend },
  } = useEditableListField<SolicitacaoExameModel>({
    name: name,
    subscription: { value: true },
  })

  const handleUpdate = useCallback(
    (formValues: SolicitacaoExameModel) => {
      const hasCitopatologico = formValues.examesSolicitados?.some(
        (exame) => exame.exame && CODIGO_CITOPATOLOGICOS.includes(exame.exame.codigo)
      )
      const hasMamografia = formValues.examesSolicitados?.some(
        (exame) => exame.exame && CODIGO_MAMOGRAFIAS.includes(exame.exame.codigo)
      )

      handleRowChanged({
        ...formValues,
        rastreioCitopatologicoColoUtero: hasCitopatologico ? formValues.rastreioCitopatologicoColoUtero : null,
        rastreioMamografia: hasMamografia ? formValues.rastreioMamografia : null,
      })
    },
    [handleRowChanged]
  )

  const editAtendProfId = getEditAtendProfId(isAtendimentoObservacao, atendimentoProfissional.id)

  const {
    data: { solicitacoesExame },
    loading,
  } = useSolicitacoesExameUnpagedQuery({
    variables: {
      input: {
        prontuarioId,
        editAtendProfId,
      },
    },
  })

  const hasSolicitacaoExames = !isEmpty(solicitacoesExameFromAtend)

  const itemsToPrint = convertSolicitacaoFromAtendToSolicitacaoWithInfo(solicitacoesExameFromAtend, {
    dataReferencia: atendimentoProfissional.iniciadoEm,
    atendimentoProfissionalId: atendimentoProfissional.id,
    lotacao: atendimentoProfissional.lotacao,
  })

  const allContent = useMemo(
    () =>
      joinSolicitacoesExame(solicitacoesExame?.content, solicitacoesExameFromAtend, {
        dataReferencia: atendimentoProfissional.iniciadoEm,
        atendimentoProfissionalId: atendimentoProfissional.id,
        lotacao: atendimentoProfissional.lotacao,
      }),
    [
      atendimentoProfissional.id,
      atendimentoProfissional.iniciadoEm,
      atendimentoProfissional.lotacao,
      solicitacoesExame,
      solicitacoesExameFromAtend,
    ]
  )

  const handleImpressaoExames = () => {
    analytics.logEvent('imprimir_solicitacoes_EXM')

    const input = convertHistoricoSolicitacaoExameToImpressao(itemsToPrint, prontuarioId, atendimentoId, cidadao.id)
    const citopatologicoPreenchido: ReadonlyArray<RastreioCancerInput> = PILOTO_SOLICITACAO_EXAME_CITOPATOLOGICO_UTERINO_ENABLED
      ? getRastreioCitoPreenchido(itemsToPrint)
      : []
    const mamografiaPreenchido: ReadonlyArray<RastreioCancerInput> = SOLICITACAO_EXAME_MAMOGRAFIA_ENABLED
      ? getRastreioMamografiaPreenchido(itemsToPrint)
      : []

    handleImpressaoPath({
      apiPath: api.solicitacaoExames.imprimirSolicitacaoExames,
      values: {
        solicitacoes: input,
        citopatologicoPreenchido: citopatologicoPreenchido,
        mamografiaPreenchido: mamografiaPreenchido,
      },
      file: 'Imprimir solicitação',
    })
      .then(() => alert('success', 'Geração da impressão de solicitações ocorreu com sucesso.'))
      .catch((error) => handleImpressaoError(error, 'Falha na impressão da solicitação.'))
  }

  const imprimirTooltip = !hasSolicitacaoExames
    ? `Não foram realizadas solicitações neste atendimento.`
    : 'Imprimir todas as solicitações deste atendimento.'
  const disabledImpressao = !hasSolicitacaoExames

  const handleSubmitSolicitacaoRegulacao = (_values: SolicitacaoRegulacaoModel) => {
    // TODO (@Sirius): Substituir por chamada à mutation real assim que
    // o endpoint de envio ao e-SUS Regulação existir no backend.
    alert('success', 'Solicitação enviada ao e-SUS Regulação com sucesso.')
  }

  return (
    <Fragment>
      {API_ESUS_REGULACAO_ENABLED && (
        <div css={classes.regulacaoHelpWrapper}>
          <ContextualHelpCard
            title='Agora o e-SUS APS está integrado ao e-SUS Regulação!'
            description={
              <>
                Exames, OCIs e Encaminhamentos podem ser enviados ao e-SUS Regulação diretamente pelo Prontuário e-SUS
                APS. Para isso, basta realizar a solicitação através do card "Solicitar via integração". Depois,
                acompanhe o andamento das solicitações em{' '}
                <strong>Garantia do acesso &gt; Regulação assistencial.</strong>
              </>
            }
            image={iconEsusRegulacao}
          />
        </div>
      )}

      <HeadingSection level={4} vSpace={8} title={SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME} />

      {API_ESUS_REGULACAO_ENABLED && (
        <Fragment>
          <div css={classes.regulacaoCardWrapper}>
            <IntegracaoRegulacaoCard
              onAdicionarSolicitacao={() => history.push(`${match.url}${SOLICITAR_REGULACAO_URL}`)}
            />
          </div>

          <Route path={`${match.url}${SOLICITAR_REGULACAO_URL}`}>
            <EsusRegulacaoModalForm
              tipoInicial={TipoSolicitacaoRegulacaoEnum.EXAME}
              tiposDisponiveis={[TipoSolicitacaoRegulacaoEnum.EXAME, TipoSolicitacaoRegulacaoEnum.LABORATORIO]}
              onClose={() => history.push(match.url)}
              onSubmit={handleSubmitSolicitacaoRegulacao}
            />
          </Route>
        </Fragment>
      )}

      <VFlow>
        {!isAtendimentoProcedimentos && <AdicionarSolicitacaoExameButtons />}
        <SolicitarExamesRootView
          cidadao={cidadao}
          handleSubmit={handleSubmit}
          handleUpdate={handleUpdate}
          adicionados={solicitacoesExameFromAtend}
          existentes={solicitacoesExame?.content}
          iniciadoEm={atendimentoProfissional.iniciadoEm}
        />

        <SolicitacaoExamesTable
          atendimentoProfissionalId={atendimentoProfissional.id}
          atendimentoId={atendimentoId}
          prontuarioId={prontuarioId}
          removeItem={removeItem}
          loading={loading}
          cidadaoId={cidadao.id}
          dataReferencia={atendimentoProfissional.iniciadoEm}
          lotacao={atendimentoProfissional.lotacao}
          isObservacaoAndAuxiliar={isObservacaoAndAuxiliar}
          items={allContent}
          solicitacoesExameFromAtend={solicitacoesExameFromAtend}
          imprimirRegistradoAgora={{
            tooltip: imprimirTooltip,
            disabled: disabledImpressao,
            handleImpressao: handleImpressaoExames,
          }}
        />
      </VFlow>
    </Fragment>
  )
}

const createStyles = () => ({
  regulacaoHelpWrapper: css`
    margin-bottom: 1rem;

    &:empty {
      margin-bottom: 0;
    }
  `,
  regulacaoCardWrapper: css`
    margin-top: 1rem;
    margin-bottom: 1rem;
  `,
})
